import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {AppFrame} from "@/app/AppFrame";
import {Grid2} from "@mui/material";
import {DataTable} from "@/lib/components/DataTable";
import {AdminAddressDTO} from "@/api/data-contracts";
import {ColumnDef} from "@tanstack/react-table";
import {adminApi} from "@/boxhub-api";
import {useSnackbarEx} from "@/lib/snackbarex";
import {AdminI18N, CommonI18N} from "@/translations";
import {AddressAdminEditDialog} from "@/pages/address-admin-page/AddressAdminEditDialog";

const AddressAdminPage = () => {
    const { t } = useTranslation();
    const { showMessage } = useSnackbarEx();
    const [addresses, setAddresses] = useState<AdminAddressDTO[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<AdminAddressDTO | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    // Incremented on every row click so the edit dialog remounts per open (see key below): the
    // dialog stays mounted after a close, and its useModelState form state would otherwise survive
    // a cancel — reopening the same row would show discarded, unsaved edits (e.g. a country change
    // abandoned via the guided legal-form dialog) instead of the persisted values.
    const [editSession, setEditSession] = useState(0);

    const loadAddresses = useCallback(() => {
        adminApi
            .getAllAddresses()
            .then((r) => setAddresses(r.data))
                .catch((error) => showMessage({
                    summary: t(AdminI18N.addressAdmin.message.loadFailed),
                    error,
                    severity: "error"
                }));
    }, [showMessage, t]);

    useEffect(() => {
        loadAddresses();
    }, [loadAddresses]);

    const handleRowClick = useCallback((row: AdminAddressDTO) => {
        setSelectedAddress(row);
        setEditSession((session) => session + 1);
        setEditOpen(true);
    }, []);

    const handleEditClose = useCallback(() => {
        setEditOpen(false);
    }, []);

    const handleEditSuccess = useCallback(() => {
        setEditOpen(false);
        loadAddresses();
    }, [loadAddresses]);

    const columns = useMemo<ColumnDef<AdminAddressDTO>[]>(
        () => [
            {
                header: t(AdminI18N.addressAdmin.column.street),
                accessorFn: (row) => row.street ?? "",
                id: "street"
            },
            {
                header: t(AdminI18N.addressAdmin.column.houseNumber),
                accessorFn: (row) => row.houseNumber ?? "",
                id: "houseNumber"
            },
            {
                header: t(AdminI18N.addressAdmin.column.zip),
                accessorFn: (row) => row.zip ?? "",
                id: "zip"
            },
            {
                header: t(AdminI18N.addressAdmin.column.city),
                accessorFn: (row) => row.city ?? "",
                id: "city"
            },
            {
                header: t(AdminI18N.addressAdmin.column.country),
                accessorFn: (row) => (row.country ? t(CommonI18N.enum.country[row.country]) : ""),
                id: "country"
            },
            {
                header: t(AdminI18N.addressAdmin.column.latitude),
                accessorFn: (row) => row.latitude ?? "",
                id: "latitude"
            },
            {
                header: t(AdminI18N.addressAdmin.column.longitude),
                accessorFn: (row) => row.longitude ?? "",
                id: "longitude"
            },
            {
                header: t(AdminI18N.addressAdmin.column.calculationStatus),
                accessorFn: (row) =>
                        row.calculationStatus ? t(CommonI18N.enum.calculationStatus[row.calculationStatus]) : "",
                id: "calculationStatus"
            },
            {
                header: t(AdminI18N.addressAdmin.column.company),
                accessorFn: (row) => row.companyName ?? "",
                id: "companyName"
            },
            {
                header: t(AdminI18N.addressAdmin.column.requestNumber),
                accessorFn: (row) => row.requestNumber ?? "",
                id: "requestNumber"
            }
        ],
        [t]
    );

    return (
            <AppFrame title={t(AdminI18N.addressAdmin.page.title)} padding={2}>
            <Grid2 container direction="column" spacing={4} paddingTop={1}>
                <Grid2 overflow="auto" width="100%">
                    <DataTable
                        columns={columns}
                        data={addresses}
                        onRowClick={handleRowClick}
                        noResultsValue={t(AdminI18N.addressAdmin.message.notFound)}
                        striped
                    />
                </Grid2>
            </Grid2>
            {selectedAddress && (
                <AddressAdminEditDialog
                        key={editSession}
                    open={editOpen}
                    close={handleEditClose}
                    address={selectedAddress}
                    onSuccess={handleEditSuccess}
                />
            )}
        </AppFrame>
    );
};

export default withAuthWrapperComponent(AddressAdminPage);
